/**
 * useWebPush — opt-in browser push subscription hook.
 *
 * Returns:
 *   - `state`: "unsupported" | "denied" | "prompt" | "granted-no-token"
 *              | "granted"
 *   - `enable()`: requests permission, fetches a token, registers with
 *                 backend, and shows a foreground toast on every push.
 *   - `disable()`: unregisters the current token (server-side + local).
 */
import * as React from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  isPushSupported,
  requestPushPermission,
  getPushToken,
  onForegroundMessage,
  registerPushToken,
  unregisterPushToken,
} from "@/lib/firebasePush";

const LOCAL_TOKEN_KEY = "sb_push_token_v1";

type PushState = "unsupported" | "denied" | "prompt" | "granted-no-token" | "granted" | "loading";

export function useWebPush(opts?: { autoOnGrant?: boolean }) {
  const { user } = useAuth();
  const [state, setState] = React.useState<PushState>("loading");
  const [token, setToken] = React.useState<string | null>(null);

  // Resolve the current permission/state.
  const refresh = React.useCallback(async () => {
    if (!isPushSupported()) { setState("unsupported"); return; }
    const perm = Notification.permission;
    if (perm === "denied") { setState("denied"); return; }
    if (perm === "default") { setState("prompt"); return; }
    // granted
    const existing = localStorage.getItem(LOCAL_TOKEN_KEY);
    if (existing) {
      setToken(existing);
      setState("granted");
    } else {
      setState("granted-no-token");
    }
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  // Auto-attach on grant so returning granted users don't have to
  // click a button — happens once per session.
  React.useEffect(() => {
    if (!opts?.autoOnGrant) return;
    if (!user || state !== "granted-no-token") return;
    (async () => {
      const t = await getPushToken();
      if (!t) { setState("granted-no-token"); return; }
      await registerPushToken(t);
      localStorage.setItem(LOCAL_TOKEN_KEY, t);
      setToken(t);
      setState("granted");
    })();
  }, [user, state, opts?.autoOnGrant]);

  // Foreground listener — the OS notification only fires when the tab
  // is backgrounded. When focused we surface a lightweight toast.
  React.useEffect(() => {
    if (state !== "granted") return;
    let unsub: (() => void) | null = null;
    (async () => {
      unsub = await onForegroundMessage((payload) => {
        const notif = payload?.notification || {};
        const data = payload?.data || {};
        toast(notif.title || "New message", {
          description: notif.body,
          action: data.click_action
            ? { label: "Open", onClick: () => { window.location.href = data.click_action; } }
            : undefined,
        });
      });
    })();
    return () => { if (unsub) unsub(); };
  }, [state]);

  const enable = React.useCallback(async () => {
    setState("loading");
    const perm = await requestPushPermission();
    if (perm !== "granted") { setState(perm === "denied" ? "denied" : "prompt"); return false; }
    const t = await getPushToken();
    if (!t) {
      // Provider missing — silently degrade so the seller can still use
      // web-sound alerts.
      setState("granted-no-token");
      toast.warning("Push isn't configured yet — you'll still get in-app sound alerts.");
      return false;
    }
    await registerPushToken(t);
    localStorage.setItem(LOCAL_TOKEN_KEY, t);
    setToken(t);
    setState("granted");
    toast.success("Push notifications enabled");
    return true;
  }, []);

  const disable = React.useCallback(async () => {
    const t = token || localStorage.getItem(LOCAL_TOKEN_KEY);
    if (t) await unregisterPushToken(t);
    localStorage.removeItem(LOCAL_TOKEN_KEY);
    setToken(null);
    setState((s) => s === "granted" ? "granted-no-token" : s);
    toast.info("Push notifications disabled");
  }, [token]);

  return { state, token, enable, disable, refresh };
}
